import { useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Tooltip,
  Divider,
  Box,
  Typography,
  Paper,
  TextField,
  Switch,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  useTheme,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  MessageSquare,
  MessageCircle,
  Info,
  PlusCircle,
  Trash,
  Sparkles,
  ShieldCheck,
  Briefcase,
  Mail,
  Palette,
} from 'lucide-react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useOrganisationActions } from '../../hooks/organisation/useOrganizationActions';
import { AppDispatch, RootState } from '../../store';
import { organizationEndpoints } from '../../services/api/endpoints/organisation.endpoint';
import { fetchOrganiationsById } from '../../store/slices/organizationSlice';
import { NotificationT, SEVERITY } from '../../types/notification';
import Notifications from '../../components/common/Notifications';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS, DEFAULT_CHAT_WIDGET_THEME } from '../../config/constants';
import { planType } from '../../types/organization';
import ChatThemeSection from './components/ChatThemeSection';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  );
};

const DEFAULT_BOOKING_CONFIRMATION_MESSAGE =
  'Thanks ${customerName}, your booking is scheduled on ${formattedDate} at ${formattedTime}. Please make sure you arrive 15 mins early to the appointment. See you';
const DEFAULT_PHONE_BOOKING_CONSENT_MESSAGE =
  'Sure! before we begin, can you please enter your phone number with country code: ${countryCode} ${country}? (Example: ${samplePhone})';
const DEFAULT_PHONE_FORM_CONSENT_MESSAGE =
  "Hey! I'd love to help you with the ${formName}. before we begin, can you please enter your phone number with country code: ${countryCode} ${country}? (Example: ${samplePhone})";

const ChatSettings = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const organization = useSelector((state: RootState) => state.organizations.selectedOrganisation);
  const { isLoading } = useOrganisationActions();
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const { role } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      isChatDisabled: organization?.isChatDisabled,
      companyName: organization?.companyName,
      chatDescription: organization?.chatDescription,
      resourceProfessional: organization?.resourceProfessional,
      company: organization?.company,
      client: organization?.client,
      planType: organization?.planType,
      isBookingEnabled: organization?.isBookingEnabled,
      bookingDescription: organization?.bookingDescription,
      clientTableName: organization?.clientTableName,
      ingest_status: organization?.ingest_status,
      chatPrefills: organization?.chatPrefills || [],
      isVerifyBySMS: organization?.isVerifyBySMS || false,
      blindCarbonCopyEmails: organization?.blindCarbonCopyEmails || [],
      newBookingConfirmationMessage:
        organization?.newBookingConfirmationMessage || DEFAULT_BOOKING_CONFIRMATION_MESSAGE,
      phoneBookingConsentMessage:
        organization?.phoneBookingConsentMessage || DEFAULT_PHONE_BOOKING_CONSENT_MESSAGE,
      phoneFormConsentMessage:
        organization?.phoneFormConsentMessage || DEFAULT_PHONE_FORM_CONSENT_MESSAGE,
      themeConfig: { ...DEFAULT_CHAT_WIDGET_THEME, ...organization?.themeConfig },
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'chatPrefills',
  });

  const {
    fields: bccFields,
    append: bccAppend,
    remove: bccRemove,
  } = useFieldArray({
    control,
    name: 'blindCarbonCopyEmails' as never,
  });

  const isChatEnabled = !watch('isChatDisabled');
  const isVerifyBySMS = watch('isVerifyBySMS');
  const watchedTheme = watch('themeConfig');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleUpdate = async (data: any) => {
    if (!organization) return;

    setIsUpdating(true);
    try {
      const updatedData = {
        ...data,
        isBookingEnabled: data.isBookingEnabled,
        bookingDescription: data.bookingDescription,
        ingest_status: data.ingest_status,
      };
      await organizationEndpoints.updateOrganisation(organization.id, updatedData);
      await dispatch(fetchOrganiationsById(organization?.id));

      setNotification({
        open: true,
        message: 'Settings updated successfully',
        severity: SEVERITY.SUCCESS,
      });
      setIsEditing(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update settings';
      setNotification({
        open: true,
        message,
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading || isUpdating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!organization) {
    return <Alert severity="info">Please select an organization to view settings.</Alert>;
  }

  const showBookingTab = organization.planType !== planType.RAG;
  const themeTabIndex = showBookingTab ? 2 : 1;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Chat & Booking Settings
        </Typography>
        {role !== AUTH_CONSTANTS.ROLES.USER && !isEditing && (
          <Button
            onClick={() => setIsEditing(true)}
            variant="contained"
            disableElevation
            className="chat-settings-tour"
          >
            Edit Settings
          </Button>
        )}
        {isEditing && (
          <Box display="flex" gap={2}>
            <Button onClick={() => setIsEditing(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={() => formRef.current?.requestSubmit()}
              variant="contained"
              disableElevation
              disabled={isUpdating}
            >
              Save Changes
            </Button>
          </Box>
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              px: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 56,
              },
            }}
          >
            <Tab
              icon={<MessageSquare size={18} />}
              iconPosition="start"
              label="Chat Options"
              sx={{ gap: 1 }}
            />
            {showBookingTab && (
              <Tab
                icon={<MessageCircle size={18} />}
                iconPosition="start"
                label="Messages"
                sx={{ gap: 1 }}
              />
            )}
            <Tab
              icon={<Palette size={18} />}
              iconPosition="start"
              label="Chat Theme"
              sx={{ gap: 1 }}
            />
          </Tabs>
        </Box>

        <form onSubmit={handleSubmit(handleUpdate)} ref={formRef}>
          {/* Chat Description Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ p: 3, pt: 0 }}>
              {/* Chat Enabled Toggle */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  mb: 3,
                  backgroundColor: isChatEnabled
                    ? `${theme.palette.success.main}10`
                    : `${theme.palette.error.main}10`,
                  borderRadius: 2,
                  border: `1px solid ${isChatEnabled ? theme.palette.success.main : theme.palette.error.main}20`,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Chat Enabled
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isChatEnabled
                      ? 'Your AI chat is active and ready to assist customers'
                      : 'Chat is currently disabled for your organization'}
                  </Typography>
                </Box>
                {isEditing ? (
                  <Controller
                    name="isChatDisabled"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={!field.value}
                        onChange={e => field.onChange(!e.target.checked)}
                        color="success"
                      />
                    )}
                  />
                ) : (
                  <Chip
                    label={isChatEnabled ? 'Enabled' : 'Disabled'}
                    color={isChatEnabled ? 'success' : 'error'}
                    size="small"
                  />
                )}
              </Box>

              {/* Chat Description */}
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Sparkles size={16} color={theme.palette.warning.main} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    AI Instructions
                  </Typography>
                  <Tooltip title="This will boost your AI's understanding and improve responses">
                    <Info size={14} color={theme.palette.text.secondary} />
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  Tell your AI what you want it to do. This will boost your AI's understanding and
                  improve responses.
                </Typography>
                {isEditing ? (
                  <Controller
                    name="chatDescription"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="e.g., You are a helpful assistant for a dental clinic. Help customers book appointments, answer questions about services, and provide information about our dental treatments..."
                        helperText="Be specific about your business, services, and how you want the AI to interact with customers"
                      />
                    )}
                  />
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.palette.action.hover,
                      borderRadius: 1,
                      minHeight: 80,
                    }}
                  >
                    <Typography variant="body2">
                      {organization.chatDescription || 'No description provided'}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Chat Prefills */}
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <MessageSquare size={16} color={theme.palette.info.main} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Chat Prefills
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  Quick action buttons shown to users when they start a conversation
                </Typography>

                {isEditing ? (
                  <>
                    <List sx={{ p: 0 }}>
                      {fields.map((field, index) => (
                        <ListItem
                          key={field.id}
                          sx={{
                            px: 0,
                            py: 0.5,
                          }}
                        >
                          <Controller
                            name={`chatPrefills.${index}`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                size="small"
                                fullWidth
                                placeholder={`e.g., Book an appointment`}
                              />
                            )}
                          />
                          <IconButton
                            onClick={() => remove(index)}
                            size="small"
                            sx={{ ml: 1 }}
                            color="error"
                          >
                            <Trash size={16} />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PlusCircle size={16} />}
                      onClick={() => append('')}
                      sx={{ mt: 1 }}
                    >
                      Add Prefill
                    </Button>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {organization.chatPrefills?.length > 0 ? (
                      organization.chatPrefills.map((prefill: string, index: number) => (
                        <Chip key={index} label={prefill} variant="outlined" size="small" />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No prefills configured
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              {/* Booking Settings */}
              {showBookingTab && (
                <>
                  <Divider sx={{ my: 3 }} />

                  {/* Customer Verification */}
                  <Box mb={3}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <ShieldCheck size={16} color={theme.palette.warning.main} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Customer Verification Method
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                      How customers will verify their identity when making bookings
                    </Typography>

                    {isEditing ? (
                      <Controller
                        name="isVerifyBySMS"
                        control={control}
                        render={({ field }) => (
                          <Box display="flex" gap={2}>
                            <Box
                              onClick={() => field.onChange(false)}
                              sx={{
                                flex: 1,
                                p: 2,
                                border: `2px solid ${!field.value ? theme.palette.primary.main : theme.palette.divider}`,
                                borderRadius: 2,
                                cursor: 'pointer',
                                backgroundColor: !field.value
                                  ? `${theme.palette.primary.main}08`
                                  : 'transparent',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: theme.palette.primary.main,
                                },
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight={600}>
                                Email Verification
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Customers verify via email OTP
                              </Typography>
                            </Box>
                            <Box
                              onClick={() => field.onChange(true)}
                              sx={{
                                flex: 1,
                                p: 2,
                                border: `2px solid ${field.value ? theme.palette.primary.main : theme.palette.divider}`,
                                borderRadius: 2,
                                cursor: 'pointer',
                                backgroundColor: field.value
                                  ? `${theme.palette.primary.main}08`
                                  : 'transparent',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: theme.palette.primary.main,
                                },
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight={600}>
                                SMS Verification
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Customers verify via SMS OTP
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      />
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: theme.palette.action.hover,
                          borderRadius: 1,
                          display: 'inline-block',
                        }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {isVerifyBySMS ? 'SMS Verification' : 'Email Verification'}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Resource Profession */}
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Briefcase size={16} color={theme.palette.info.main} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        What kind of profession are you in?
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                      This helps the AI understand your business context better
                    </Typography>

                    {isEditing ? (
                      <Controller
                        name="resourceProfessional"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            value={field.value === false ? '' : field.value}
                            placeholder="e.g., Doctors, Hairdressers, Therapists, Consultants"
                            helperText="Enter the type of professionals in your organization"
                          />
                        )}
                      />
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: theme.palette.action.hover,
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2">
                          {organization.resourceProfessional || 'Not specified'}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* BCC Emails for Booking Confirmations */}
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Mail size={16} color={theme.palette.info.main} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Send Booking Confirmation Emails Copy To
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                      These email addresses will receive a BCC copy of all booking confirmation,
                      reschedule, and cancellation emails
                    </Typography>

                    {isEditing ? (
                      <>
                        <List sx={{ p: 0 }}>
                          {bccFields.map((field, index) => (
                            <ListItem
                              key={field.id}
                              sx={{
                                px: 0,
                                py: 0.5,
                              }}
                            >
                              <Controller
                                name={`blindCarbonCopyEmails.${index}` as never}
                                control={control}
                                rules={{
                                  pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: 'Invalid email address',
                                  },
                                }}
                                render={({ field, fieldState }) => (
                                  <TextField
                                    {...field}
                                    size="small"
                                    fullWidth
                                    type="email"
                                    placeholder="e.g., manager@company.com"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                  />
                                )}
                              />
                              <IconButton
                                onClick={() => bccRemove(index)}
                                size="small"
                                sx={{ ml: 1 }}
                                color="error"
                              >
                                <Trash size={16} />
                              </IconButton>
                            </ListItem>
                          ))}
                        </List>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PlusCircle size={16} />}
                          onClick={() => bccAppend('' as never)}
                          sx={{ mt: 1 }}
                        >
                          Add Email
                        </Button>
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {organization.blindCarbonCopyEmails &&
                        organization.blindCarbonCopyEmails.length > 0 ? (
                          organization.blindCarbonCopyEmails.map((email: string, index: number) => (
                            <Chip key={index} label={email} variant="outlined" size="small" />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No BCC emails configured
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </TabPanel>

          {/* Messages Tab */}
          {showBookingTab && (
            <TabPanel value={activeTab} index={1}>
              <Box sx={{ p: 3, pt: 0 }}>
                {/* Booking Confirmation Message */}
                <Box mb={3}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <MessageCircle size={16} color={theme.palette.success.main} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Booking Confirmation Message
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                    Message sent to the customer after a booking is confirmed
                  </Typography>
                  {isEditing ? (
                    <Controller
                      name="newBookingConfirmationMessage"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={3}
                          placeholder={DEFAULT_BOOKING_CONFIRMATION_MESSAGE}
                          helperText="Available placeholders: ${customerName}, ${formattedDate}, ${formattedTime}"
                        />
                      )}
                    />
                  ) : (
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 1,
                        minHeight: 60,
                      }}
                    >
                      <Typography variant="body2">
                        {organization.newBookingConfirmationMessage ||
                          DEFAULT_BOOKING_CONFIRMATION_MESSAGE}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Phone Booking Consent Message */}
                <Box mb={3}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <MessageCircle size={16} color={theme.palette.info.main} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Phone Booking Consent Message
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                    Message asking the customer for their phone number before booking
                  </Typography>
                  {isEditing ? (
                    <Controller
                      name="phoneBookingConsentMessage"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={3}
                          placeholder={DEFAULT_PHONE_BOOKING_CONSENT_MESSAGE}
                          helperText="Available placeholders: ${countryCode}, ${country}, ${samplePhone}"
                        />
                      )}
                    />
                  ) : (
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 1,
                        minHeight: 60,
                      }}
                    >
                      <Typography variant="body2">
                        {organization.phoneBookingConsentMessage ||
                          DEFAULT_PHONE_BOOKING_CONSENT_MESSAGE}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Phone Form Consent Message */}
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <MessageCircle size={16} color={theme.palette.warning.main} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Phone Form Consent Message
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                    Message asking for the customer's phone number before filling a form
                  </Typography>
                  {isEditing ? (
                    <Controller
                      name="phoneFormConsentMessage"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={3}
                          placeholder={DEFAULT_PHONE_FORM_CONSENT_MESSAGE}
                          helperText="Available placeholders: ${formName}, ${countryCode}, ${country}, ${samplePhone}"
                        />
                      )}
                    />
                  ) : (
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 1,
                        minHeight: 60,
                      }}
                    >
                      <Typography variant="body2">
                        {organization.phoneFormConsentMessage || DEFAULT_PHONE_FORM_CONSENT_MESSAGE}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </TabPanel>
          )}

          {/* Chat Theme Tab */}
          <TabPanel value={activeTab} index={themeTabIndex}>
            <Box sx={{ p: 3, pt: 0 }}>
              <ChatThemeSection
                control={control}
                setValue={setValue}
                watchedTheme={watchedTheme}
                isEditing={isEditing}
                organization={organization}
              />
            </Box>
          </TabPanel>
        </form>
      </Paper>

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ChatSettings;
